import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ShowcaseHighlight as ShowcaseHighlightData } from '@/nova/mock-data/types';
import { ShowcaseHighlight } from './ShowcaseHighlight';
import { VideoStage } from './VideoStage';
import { autoplayDwellMs } from './useShowcaseReveal';
import {
  Centre,
  CornerGlow,
  EmptyNote,
  Eyebrow,
  Fallback,
  FallbackIcon,
  FallbackName,
  FallbackNote,
  Heading,
  HeadingTitle,
  LeftColumn,
  Root,
  Stack,
} from './FeatureShowcase.styles';

export type FeatureShowcaseProps = {
  /** Talking points for the active tier (title + typed description + optional clip). */
  highlights: ShowcaseHighlightData[];
  /** Small uppercase eyebrow (the tier's highlights-header title, else the feature name). */
  eyebrow: string;
  /** Large heading (the tier's highlights-header description, else "How it works?"). */
  title: string;
  /** When this changes the sequence restarts from the first highlight (feature/tier switch). */
  resetKey: string;
  /** Feature icon + name for the centre fallback when a highlight has no playable clip. */
  fallbackImage?: string;
  fallbackName: string;
};

/** All collapsed (no talking point expanded) — set when the active card is collapsed. */
const NONE = -1;

/**
 * The product feature-detail showcase: the animated talking-point stack (left) and
 * a per-highlight demo clip (centre), replacing the static hero. A re-skin of the
 * configurator-v3 features-step experience onto the product page's own surfaces.
 *
 * On mount / `resetKey` change the first highlight auto-expands; during autoplay each
 * highlight stays active for {@link autoplayDwellMs} (`(clip + read time) × 2`), with a
 * depleting ring counting it down, then the next opens. It plays through the set once,
 * then stops (the ring becomes a collapse button). A manual click takes over and stops
 * the sequence too.
 */
export const FeatureShowcase = ({
  highlights,
  eyebrow,
  title,
  resetKey,
  fallbackImage,
  fallbackName,
}: FeatureShowcaseProps): React.ReactElement => {
  const len = highlights.length;

  const [active, setActive] = useState(0);
  // Whether the sequence is still auto-advancing (drives ring vs collapse-button).
  const [autoplaying, setAutoplaying] = useState(len > 1);
  // Cards that have completed their reveal once show instantly on re-open. A ref
  // (not state) so marking one done doesn't re-render + re-run its own reveal.
  const seenRef = useRef<Set<number>>(new Set());
  const advanceTimerRef = useRef<number | null>(null);

  const clearAdvance = useCallback(() => {
    if (advanceTimerRef.current != null) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, []);

  // Restart the sequence from the first highlight whenever the set changes.
  useEffect(() => {
    clearAdvance();
    seenRef.current = new Set();
    setActive(0);
    setAutoplaying(len > 1);
  }, [resetKey, len, clearAdvance]);

  // Auto-advance: while autoplaying, hold the active highlight for its dwell, then open
  // the next. Stops ON the last highlight (no wrap back to the first).
  useEffect(() => {
    if (!autoplaying || len <= 1 || active < 0) return undefined;
    const highlight = highlights[active];
    if (!highlight) return undefined;

    advanceTimerRef.current = window.setTimeout(() => {
      advanceTimerRef.current = null;
      if (active + 1 >= len) {
        setAutoplaying(false); // reached the end — stop, leaving the last card open
      } else {
        setActive(active + 1);
      }
    }, autoplayDwellMs(highlight));

    return clearAdvance;
  }, [active, autoplaying, len, highlights, clearAdvance]);

  // Manual click takes over: cancel the pending advance, jump, and stop the sequence.
  const onCardClick = useCallback(
    (index: number) => {
      clearAdvance();
      setAutoplaying(false);
      setActive(index);
    },
    [clearAdvance],
  );

  // Collapse the expanded card (the corner button, shown once autoplay has stopped).
  const onCollapse = useCallback(() => {
    clearAdvance();
    setAutoplaying(false);
    setActive(NONE);
  }, [clearAdvance]);

  // Mark a card seen so re-opening it shows the end-state instantly (no re-typing).
  const onCardReveal = useCallback((index: number) => {
    seenRef.current.add(index);
  }, []);

  const activeHighlight = active >= 0 ? highlights[active] : undefined;
  const dwellMs = activeHighlight ? autoplayDwellMs(activeHighlight) : 0;

  const fallback = (
    <Fallback>
      {fallbackImage ? <FallbackIcon src={fallbackImage} alt="" /> : null}
      <FallbackName text={fallbackName} />
      <FallbackNote text="Preview unavailable on this domain" />
    </Fallback>
  );

  return (
    <Root>
      <LeftColumn>
        <Heading>
          <Eyebrow text={eyebrow} />
          <HeadingTitle text={title} tag="h2" />
        </Heading>

        {len > 0 ? (
          <Stack>
            {highlights.map((highlight, index) => (
              <ShowcaseHighlight
                key={highlight.id}
                highlight={highlight}
                active={index === active}
                instant={seenRef.current.has(index)}
                // While this card is the autoplaying one, the ring counts down its dwell;
                // otherwise (active but stopped) it shows the collapse button instead.
                countdownMs={index === active && autoplaying ? dwellMs : undefined}
                onClick={() => onCardClick(index)}
                onCollapse={onCollapse}
                onRevealComplete={() => onCardReveal(index)}
              />
            ))}
          </Stack>
        ) : (
          <EmptyNote text="No highlights yet." />
        )}
      </LeftColumn>

      <Centre>
        <VideoStage video={activeHighlight?.video} fallback={fallback} />
      </Centre>

      {/* Ambient corner highlights — on top of the whole stage + content, no pointer events. */}
      <CornerGlow aria-hidden />
    </Root>
  );
};

export default FeatureShowcase;
